
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Trophy, Medal, Award } from 'lucide-react';

interface LeaderboardPlayer {
  id: string;
  name: string;
  points: number;
  gamesPlayed: number;
  roundScores?: number[];
  matchesPlayed?: number;
}

interface TournamentLeaderboardProps {
  players: LeaderboardPlayer[];
  currentRound: number;
  isFinished: boolean;
  tournamentType: 'mexicano' | 'americano';
}

export const TournamentLeaderboard: React.FC<TournamentLeaderboardProps> = ({
  players,
  currentRound,
  isFinished,
  tournamentType
}) => {
  const { language } = useLanguage();

  const leaderboard = [...players].sort((a, b) => {
    // For Mexicano: sort by total match points scored (roundScores) only
    if (tournamentType === 'mexicano') {
      const aTotalMatchPoints = (a.roundScores || []).reduce((sum, score) => sum + score, 0);
      const bTotalMatchPoints = (b.roundScores || []).reduce((sum, score) => sum + score, 0);
      
      // Primary sort: total match points scored across all rounds
      if (bTotalMatchPoints !== aTotalMatchPoints) {
        return bTotalMatchPoints - aTotalMatchPoints;
      }
      
      // Tiebreaker: matches played
      return (b.gamesPlayed || b.matchesPlayed || 0) - (a.gamesPlayed || a.matchesPlayed || 0);
    }
    
    // For Americano: sort by tournament points, then by matches played
    if (b.points !== a.points) return b.points - a.points;
    return (b.gamesPlayed || b.matchesPlayed || 0) - (a.gamesPlayed || a.matchesPlayed || 0);
  });

  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Trophy className="h-4 w-4 text-yellow-500" />;
      case 2:
        return <Medal className="h-4 w-4 text-gray-400" />;
      case 3:
        return <Award className="h-4 w-4 text-orange-600" />;
      default:
        return null;
    }
  };

  const getPositionBadge = (position: number) => {
    if (position <= 3) {
      const colors = ['bg-yellow-100 text-yellow-800', 'bg-gray-100 text-gray-800', 'bg-orange-100 text-orange-800'];
      return (
        <Badge variant="secondary" className={colors[position - 1]}>
          {position}
        </Badge>
      );
    }
    return <span className="font-semibold">{position}</span>;
  };

  const completedRounds = currentRound - (isFinished ? 0 : 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-green-500" />
          {isFinished 
            ? (language === 'no' ? 'Sluttresultat' : 'Final Results')
            : (language === 'no' ? 'Resultater' : 'Leaderboard')
          }
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">
                  {language === 'no' ? 'Plass' : 'Pos'}
                </TableHead>
                <TableHead>{language === 'no' ? 'Spiller' : 'Player'}</TableHead>
                <TableHead className="text-center font-bold">
                  {language === 'no' ? 'Total' : 'Total'}
                </TableHead>
                {Array.from({ length: completedRounds }, (_, i) => (
                  <TableHead key={i} className="text-center">
                    {language === 'no' ? `R${i + 1}` : `R${i + 1}`}
                  </TableHead>
                ))}
                <TableHead className="text-center">
                  {language === 'no' ? 'Kamper' : 'Matches'}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaderboard.map((player, index) => {
                const position = index + 1;
                const roundScores = player.roundScores || [];
                const matchesPlayed = player.gamesPlayed || player.matchesPlayed || 0;
                
                return (
                  <TableRow key={player.id} className={position <= 3 ? 'bg-muted/50' : ''}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {getPositionBadge(position)}
                        {getPositionIcon(position)}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{player.name}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={position === 1 ? 'default' : 'secondary'} className="font-bold">
                        {tournamentType === 'mexicano' 
                          ? (player.roundScores || []).reduce((sum, score) => sum + score, 0)
                          : player.points
                        }
                      </Badge>
                    </TableCell>
                    {Array.from({ length: completedRounds }, (_, i) => (
                      <TableCell key={i} className="text-center">
                        {roundScores[i] !== undefined ? roundScores[i] : '-'}
                      </TableCell>
                    ))}
                    <TableCell className="text-center">{matchesPlayed}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {isFinished && leaderboard.length > 0 && (
          <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 text-green-800">
              <Trophy className="h-5 w-5" />
              <span className="font-semibold">
                {language === 'no' 
                  ? `🎉 Gratulerer til ${leaderboard[0].name}!` 
                  : `🎉 Congratulations to ${leaderboard[0].name}!`
                }
              </span>
            </div>
            <p className="text-sm text-green-700 mt-1">
              {language === 'no'
                ? `Vant med ${leaderboard[0].points} poeng på ${leaderboard[0].gamesPlayed || leaderboard[0].matchesPlayed} kamper`
                : `Won with ${leaderboard[0].points} points in ${leaderboard[0].gamesPlayed || leaderboard[0].matchesPlayed} games`
              }
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
